import { useQuery } from '@apollo/client'
import { Scan, Lock, Globe, Waypoints } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { IS_RATE_LIMITING_ENABLED } from '@/components/shared/rate-limiting/config'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { LOAD_BALANCER_DISPLAY_NAME_QUERY } from '@/routes/load-balancer/queries'
import type {
  LoadBalancerDisplayNameQuery,
  LoadBalancerDisplayNameQueryVariables,
} from '@/routes/load-balancer/queries.types.gen'

const items = [
  {
    title: 'Overview',
    url: '/load-balancer/:loadBalancerName/overview',
    icon: Scan,
    activeTags: ['/overview'],
  },
  {
    title: 'Domains',
    url: '/load-balancer/:loadBalancerName/domains',
    icon: Globe,
    activeTags: ['/domains'],
  },
  {
    title: 'Networking',
    url: '/load-balancer/:loadBalancerName/networking',
    icon: Waypoints,
    activeTags: ['/networking'],
  },
] as const

const visibleItems = items.filter(
  (item) => item.title !== 'Networking' || IS_RATE_LIMITING_ENABLED
)

export const LoadBalancerNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { loadBalancerName = '' } = useParams<{ loadBalancerName: string }>()
  const { data } = useQuery<
    LoadBalancerDisplayNameQuery,
    LoadBalancerDisplayNameQueryVariables
  >(LOAD_BALANCER_DISPLAY_NAME_QUERY, {
    variables: { idCompany, loadBalancerName },
    fetchPolicy: 'cache-only',
  })

  const idLoadBalancer = useMemo(
    () => data?.company?.edgeLoadBalancer?.id ?? '',
    [data]
  )
  const idProjectLoadBalancer = useMemo(
    () => data?.company?.edgeLoadBalancer?.idProject ?? '',
    [data]
  )
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac
            .company(idCompany)
            .project(idProjectLoadBalancer)
            .loadBalancer(idLoadBalancer)
            .view.can(),
    [rbac, idCompany, idLoadBalancer, idProjectLoadBalancer, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { loadBalancerName },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
